import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../components/ui/RoleBasedHeader';
import Button from '../components/ui/Button';
import { authService } from '../services/supabaseService';
import { supabase } from '../lib/supabase';
import { APP_NAME } from '../constants';

const ServicesZone = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [serviceZones, setServiceZones] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // Fetch service zones
  useEffect(() => {
    console.log('Component mounted');
    fetchServiceZones();
  }, []);

  const fetchServiceZones = async () => {
    try {
      console.log('Fetching service zones...');
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { data, error: fetchError } = await supabase
        .from('service_zones')
        .select('*')
        .order('priority', { ascending: true })
        .order('name', { ascending: true });

      console.log('Fetch result:', { data, fetchError });

      if (fetchError) {
        console.error('Supabase error:', fetchError);
        throw fetchError;
      }

      console.log('Setting zones:', data);
      setServiceZones(data || []);
      
    } catch (err) {
      console.error('Error in fetchServiceZones:', err);
      setError('Erreur: ' + err.message);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleCreateZone = async (zoneData) => {
    try {
      setLoading(true);
      const { data, error: insertError } = await supabase
        .from('service_zones')
        .insert([zoneData])
        .select();

      if (insertError) throw insertError;

      setServiceZones([...serviceZones, ...data]);
      setShowModal(false);
      setError(null);
    } catch (err) {
      console.error('Error creating service zone:', err);
      setError('Erreur lors de la création de la zone: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('service_zones')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setServiceZones(serviceZones.filter(zone => zone.id !== id));
    } catch (err) {
      console.error('Error deleting service zone:', err);
      setError('Erreur lors de la suppression de la zone: ' + err.message);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const { error: updateError } = await supabase
        .from('service_zones')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setServiceZones(serviceZones.map(zone => 
        zone.id === id ? { ...zone, is_active: !currentStatus } : zone
      ));
    } catch (err) {
      console.error('Error toggling zone status:', err);
      setError('Erreur lors de la mise à jour du statut: ' + err.message);
    }
  };

  console.log('Current state:', { loading, error, zonesCount: serviceZones.length });

  if (loading && serviceZones.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole={currentUser?.role || 'admin'}
        userName={currentUser?.name || `Admin ${APP_NAME}`}
      />

      <main className="pt-16 transition-layout">
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-800 hover:text-red-900 font-bold">×</button>
            </div>
          )}

          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Zones de service</h1>
              <p className="text-muted-foreground mt-1">
                Gérer les zones de service et leur disponibilité ({serviceZones.length} zones)
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                iconName="Download" 
                iconPosition="left" 
                iconSize={16}
                onClick={() => {
                  const csv = [
                    ['Nom', 'Code Postal', 'Municipalité', 'Rayon (km)', 'Actif', 'Priorité'],
                    ...serviceZones.map(z => [z.name, z.postal_code || '', z.municipality_name || '', z.radius_km, z.is_active ? 'Oui' : 'Non', z.priority])
                  ].map(row => row.join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'zones-service.csv';
                  a.click();
                }}
              >
                Exporter
              </Button>
              <Button 
                variant="default" 
                iconName="Plus" 
                iconPosition="left" 
                iconSize={16}
                onClick={() => setShowModal(true)}
              >
                Nouvelle zone
              </Button>
            </div>
          </div>

          {/* Main Content - Zones List */}
          <div className="bg-card rounded-lg border border-border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="text-left p-4 font-semibold text-sm">Nom</th>
                    <th className="text-left p-4 font-semibold text-sm">Code Postal</th>
                    <th className="text-left p-4 font-semibold text-sm">Municipalité</th>
                    <th className="text-left p-4 font-semibold text-sm">Rayon (km)</th>
                    <th className="text-left p-4 font-semibold text-sm">Priorité</th>
                    <th className="text-left p-4 font-semibold text-sm">Statut</th>
                    <th className="text-left p-4 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceZones.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center p-8 text-muted-foreground">
                        Aucune zone de service configurée
                      </td>
                    </tr>
                  ) : (
                    serviceZones.map((zone) => (
                      <tr key={zone.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-4 font-medium">{zone.name}</td>
                        <td className="p-4 text-muted-foreground">{zone.postal_code || '-'}</td>
                        <td className="p-4 text-muted-foreground">{zone.municipality_name || '-'}</td>
                        <td className="p-4 text-muted-foreground">{zone.radius_km || '-'}</td>
                        <td className="p-4 text-muted-foreground">{zone.priority}</td>
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              zone.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {zone.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleActive(zone.id, zone.is_active)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              {zone.is_active ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              onClick={() => handleDeleteZone(zone.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Create Zone Modal */}
      {showModal && (
        <CreateZoneModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreateZone}
        />
      )}
    </div>
  );
};

// Create Zone Modal Component
const CreateZoneModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    postal_code: '',
    municipality_name: '',
    latitude: '',
    longitude: '',
    radius_km: '5.0',
    priority: '1',
    is_active: true,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one location field is filled
    if (!formData.postal_code && !formData.municipality_name && (!formData.latitude || !formData.longitude)) {
      alert('Veuillez remplir au moins un champ de localisation (Code postal, Municipalité, ou Latitude/Longitude)');
      return;
    }

    setSubmitting(true);
    
    const dataToSubmit = {
      name: formData.name,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      radius_km: parseFloat(formData.radius_km),
      priority: parseInt(formData.priority),
      postal_code: formData.postal_code || null,
      municipality_name: formData.municipality_name || null,
      notes: formData.notes || null,
      is_active: formData.is_active
    };

    await onCreate(dataToSubmit);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold">Nouvelle zone de service</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nom de la zone *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Code postal</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Municipalité</label>
              <input
                type="text"
                value={formData.municipality_name}
                onChange={(e) => setFormData({...formData, municipality_name: e.target.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Latitude</label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Longitude</label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Rayon (km)</label>
              <input
                type="number"
                step="0.1"
                value={formData.radius_km}
                onChange={(e) => setFormData({...formData, radius_km: e.target.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Priorité</label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="mr-2"
            />
            <label htmlFor="is_active" className="text-sm font-medium">Zone active</label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button type="submit" variant="default" disabled={submitting}>
              {submitting ? 'Création...' : 'Créer la zone'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServicesZone;